import { PaginationProductType, UserData } from '@/api';
import { useState, useCallback } from 'react';

export const CreateEditCaseSidebarFormDataFieldsName = {
  CaseReference: 'caseReference',
  GReference: 'gReference',
  AllegationDocuments: 'allegationDocuments',
  AssignedUser: 'assignedUser',
  DueDate: 'dueDate',
  Urgent: 'urgent',
  RunGdpr: 'runGdpr',
  // Optional client-detail fields. Surface in the sidebar's "Optional
  // fields" accordion; persisted via case/case-info/edit (not case/edit).
  ClientName: 'clientName',
  ClientDob: 'clientDob',
  // Optional instructing-company field (MLS org only). Selection sends
  // `instructing_company_id` on case/create and `instructingCompanyId`
  // on case/edit; clearing the dropdown sends null to unset it.
  InstructingCompany: 'instructingCompany',
} as const;

export type BundleTypeKeys =
  (typeof CreateEditCaseSidebarFormDataFieldsName)[keyof typeof CreateEditCaseSidebarFormDataFieldsName];

export const productTypeOptions = [
  {
    name: 'Light pagination',
    code: PaginationProductType.LIGHT,
  },
  {
    name: 'Light pagination (without GDPR)',
    code: PaginationProductType.LIGHT_WITHOUT_GDPR,
  },
  {
    name: 'Full pagination',
    code: PaginationProductType.FULL,
  },
  {
    name: 'Full pagination (without GDPR)',
    code: PaginationProductType.FULL_WITHOUT_GDPR,
  },
  {
    name: 'GDPR only',
    code: PaginationProductType.GDPR_ONLY,
  },
];

export interface CreateEditCaseSidebarFormData {
  [CreateEditCaseSidebarFormDataFieldsName.CaseReference]: string;
  [CreateEditCaseSidebarFormDataFieldsName.GReference]: string;
  [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: string[];
  [CreateEditCaseSidebarFormDataFieldsName.AssignedUser]: UserData | null;
  [CreateEditCaseSidebarFormDataFieldsName.DueDate]: Date;
  [CreateEditCaseSidebarFormDataFieldsName.Urgent]: boolean;
  [CreateEditCaseSidebarFormDataFieldsName.RunGdpr]: boolean;
  [CreateEditCaseSidebarFormDataFieldsName.ClientName]: string;
  [CreateEditCaseSidebarFormDataFieldsName.ClientDob]: Date | null;
  [CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]: string | null;
}

type FormDataValueTypes = CreateEditCaseSidebarFormData[keyof CreateEditCaseSidebarFormData];

const initialFormState: CreateEditCaseSidebarFormData = {
  [CreateEditCaseSidebarFormDataFieldsName.CaseReference]: '',
  [CreateEditCaseSidebarFormDataFieldsName.GReference]: '',
  [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: [],
  [CreateEditCaseSidebarFormDataFieldsName.AssignedUser]: null,
  [CreateEditCaseSidebarFormDataFieldsName.DueDate]: new Date(),
  [CreateEditCaseSidebarFormDataFieldsName.Urgent]: false,
  [CreateEditCaseSidebarFormDataFieldsName.RunGdpr]: false,
  [CreateEditCaseSidebarFormDataFieldsName.ClientName]: '',
  [CreateEditCaseSidebarFormDataFieldsName.ClientDob]: null,
  [CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]: null,
};

export const useCreateEditCaseForm = () => {
  const [formData, setFormData] = useState<CreateEditCaseSidebarFormData>(initialFormState);

  const handleValueChange = useCallback((fieldName: keyof CreateEditCaseSidebarFormData, value: FormDataValueTypes) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialFormState);
  }, []);

  const isFormValid = useCallback((data: CreateEditCaseSidebarFormData) => {
    return !!data.caseReference && data.allegationDocuments?.length > 0 && data.dueDate !== null;
  }, []);

  const canSubmit = isFormValid(formData);

  return {
    formData,
    setFormData,
    handleValueChange,
    resetForm,
    canSubmit,
  };
};
